import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { paymentsAPI, bookingsAPI, extractErrorMessage } from '../api';

const Payments = () => {
  const { selectedStore } = useStore();
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bookingSummary, setBookingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [formData, setFormData] = useState({
    booking_id: '', amount: '', payment_type: '', payment_method: '', reference_number: '', notes: '',
  });

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedBookingId) params.booking_id = selectedBookingId;
      const response = await paymentsAPI.list(selectedStore.id, params);
      setPayments(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load payments'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore, selectedBookingId]);

  const loadBookings = useCallback(async () => {
    try {
      const response = await bookingsAPI.list(selectedStore.id);
      setBookings(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  const loadOptions = useCallback(async () => {
    try {
      const [typesRes, methodsRes] = await Promise.allSettled([
        paymentsAPI.getTypes(),
        paymentsAPI.getMethods(),
      ]);
      if (typesRes.status === 'fulfilled') {
        const d = typesRes.value.data;
        setPaymentTypes(Array.isArray(d) ? d : d.types || ['deposit', 'rental_fee', 'damage_fee', 'late_fee', 'other']);
      } else {
        setPaymentTypes(['deposit', 'rental_fee', 'damage_fee', 'late_fee', 'other']);
      }
      if (methodsRes.status === 'fulfilled') {
        const d = methodsRes.value.data;
        setPaymentMethods(Array.isArray(d) ? d : d.methods || ['cash', 'card', 'bank_transfer', 'mobile_payment']);
      } else {
        setPaymentMethods(['cash', 'card', 'bank_transfer', 'mobile_payment']);
      }
    } catch (err) {
      setPaymentTypes(['deposit', 'rental_fee', 'damage_fee', 'late_fee', 'other']);
      setPaymentMethods(['cash', 'card', 'bank_transfer', 'mobile_payment']);
    }
  }, []);

  useEffect(() => {
    if (selectedStore) {
      loadPayments();
      loadBookings();
      loadOptions();
    }
  }, [selectedStore, loadPayments, loadBookings, loadOptions]);

  const loadBookingSummary = async (bookingId) => {
    if (!bookingId) { setBookingSummary(null); return; }
    try {
      const response = await paymentsAPI.getBookingSummary(bookingId);
      setBookingSummary(response.data);
    } catch (err) {
      setBookingSummary(null);
    }
  };

  const handleBookingSelect = (bookingId) => {
    setSelectedBookingId(bookingId);
    if (bookingId) loadBookingSummary(bookingId);
    else setBookingSummary(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, store_id: selectedStore.id, amount: parseFloat(formData.amount) };
      await paymentsAPI.create(data);
      loadPayments();
      if (formData.booking_id) loadBookingSummary(formData.booking_id);
      resetForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const resetForm = () => {
    setFormData({ booking_id: '', amount: '', payment_type: '', payment_method: '', reference_number: '', notes: '' });
    setShowForm(false);
    setError(null);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading payments...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Payments</h1>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}>
          {showForm ? 'Cancel' : '+ Record Payment'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="filter-bar">
        <div className="form-group">
          <label className="form-label">Filter by Booking</label>
          <select className="form-select" value={selectedBookingId}
            onChange={(e) => handleBookingSelect(e.target.value)}>
            <option value="">All Bookings</option>
            {bookings.map(b => (
              <option key={b.id} value={b.id}>#{b.booking_number || b.id} - {b.customer_name || b.customer?.name || 'Customer'}</option>
            ))}
          </select>
        </div>
      </div>

      {bookingSummary && (
        <div className="kpi-grid">
          <div className="kpi-card primary">
            <div className="kpi-label">Total Due</div>
            <div className="kpi-value">${parseFloat(bookingSummary.total_due || 0).toFixed(2)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Total Paid</div>
            <div className="kpi-value">${parseFloat(bookingSummary.total_paid || 0).toFixed(2)}</div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-label">Balance</div>
            <div className="kpi-value">${parseFloat(bookingSummary.balance || 0).toFixed(2)}</div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">Record Payment</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Booking *</label>
                  <select className="form-select" value={formData.booking_id}
                    onChange={(e) => setFormData({ ...formData, booking_id: parseInt(e.target.value) })} required>
                    <option value="">Select Booking</option>
                    {bookings.map(b => (
                      <option key={b.id} value={b.id}>#{b.booking_number || b.id} - {b.customer_name || b.customer?.name || 'Customer'}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Amount *</label>
                  <input type="number" className="form-input" value={formData.amount} step="0.01" min="0"
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Type *</label>
                  <select className="form-select" value={formData.payment_type}
                    onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })} required>
                    <option value="">Select Type</option>
                    {paymentTypes.map(t => { const val = typeof t === 'string' ? t : t.name || t.value || String(t); return <option key={val} value={val}>{val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>; })}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Method *</label>
                  <select className="form-select" value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} required>
                    <option value="">Select Method</option>
                    {paymentMethods.map(m => { const val = typeof m === 'string' ? m : m.name || m.value || String(m); return <option key={val} value={val}>{val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>; })}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Reference Number</label>
                  <input type="text" className="form-input" value={formData.reference_number}
                    onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={formData.notes} rows={2}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Record Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Cards View */}
      <div className="mobile-cards hidden-desktop">
        {payments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💳</div>
            <h3 className="empty-state-title">No payments found</h3>
          </div>
        ) : (
          payments.map(payment => (
            <div className="mobile-card" key={payment.id}>
              <div className="mobile-card-header">
                <div className="mobile-card-title">${parseFloat(payment.amount).toFixed(2)}</div>
                <span className="badge badge-info">{payment.payment_type?.replace(/_/g, ' ')}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Booking</span>
                <span>#{payment.booking_number || payment.booking_id}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Method</span>
                <span>{payment.payment_method?.replace(/_/g, ' ')}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Date</span>
                <span>{payment.created_at ? new Date(payment.created_at).toLocaleDateString() : '-'}</span>
              </div>
              {payment.reference_number && (
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Reference</span>
                  <span>{payment.reference_number}</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="card desktop-table">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Booking</th><th>Amount</th><th>Type</th><th>Method</th><th>Reference</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(payment => (
                <tr key={payment.id}>
                  <td>{payment.created_at ? new Date(payment.created_at).toLocaleDateString() : '-'}</td>
                  <td>#{payment.booking_number || payment.booking_id}</td>
                  <td className="font-bold">${parseFloat(payment.amount).toFixed(2)}</td>
                  <td><span className="badge badge-info">{payment.payment_type?.replace(/_/g, ' ')}</span></td>
                  <td>{payment.payment_method?.replace(/_/g, ' ')}</td>
                  <td className="text-muted">{payment.reference_number || '-'}</td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr><td colSpan="6" className="text-center text-muted" style={{ padding: '3rem' }}>No payments found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Payments;
